import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import FormLayout from "components/form/FormLayout";
import Sheet from "components/sheet/Sheet";
import TimePick from "components/timePick/TimePick";
import { useSearch } from "hooks/useSearch/useSearch";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ILeague } from "redux/reducers/models/league";
import { ITeeTimeNew } from "redux/reducers/models/teetime";
import { ITournament } from "redux/reducers/models/tournament";
import { Select } from "components/select/index";
import Icon from "components/icon/Icon";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import moment, { MomentInput } from "moment";
import { StatusCode } from "api/protocols";
import { SkeletonBar } from "components/skeleton";
import ReactDOM from "react-dom";
import axios, { CancelToken } from "axios";
import { GetEditShotgunPreview } from "api/rpc/2024-04/facilityAdmin/teesheet/shotgun";
import Callout from "components/callout/Callout";

interface IProps {
    open: boolean;
    teetime: ITeeTimeNew;
    teetimes: Array<ITeeTimeNew>;
    onClose: () => void;
    onOk: ({ start_time, league_id, tournament_id, duration, lead_time, shotgun_application_id, tee_sheet_id }: { start_time: string; league_id: number; tournament_id: number; duration: string; lead_time: string; shotgun_application_id: number; tee_sheet_id: number }) => void;
}

interface IState {
    start_time: string;
    selectedLeague: ILeague;
    selectedTournament: ITournament;
    duration: string;
    lead_time: string;
}

export default function EditShotgunModal(props: IProps) {
    const { teetime, teetimes, open, onClose, onOk } = props;
    const { t } = useTranslation();
    const { Option } = Select;
    const [loading, setLoading] = useState<boolean>(false);
    const [state, setState] = useState<IState>({
        start_time: "00:00",
        selectedLeague: null,
        selectedTournament: null,
        duration: "00:00",
        lead_time: "00:00",
    });

    const [errors, setErrors] = useState<{duration: boolean; lead_time: boolean, message: string}>({
        duration: false,
        lead_time: false,
        message: "",
    });
    const leaguesHook = useSearch<ILeague>({
        fetchAll: () => GetLeague(null, false).then(res => res.data),
        searchFn: search =>
            GetLeague({ search }, false).then(res => res.data),
    });

    const tournamentsHook = useSearch<ITournament>({
        fetchAll: () => GetTournament(null, false).then(res => res.data),
        searchFn: search =>
            GetTournament({ search }, false).then(res => res.data),
    });

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (open && teetimes && teetime) {
            void calculateShotgunValues(teetimes, source.token);
        } else if (!open) {
            ReactDOM.unstable_batchedUpdates(() => {
                setLoading(true);
                setState({
                    start_time: "00:00",
                    selectedLeague: null,
                    selectedTournament: null,
                    duration: "00:00",
                    lead_time: "00:00",
                });
            });
        }

        return () => source.cancel();
    }, [teetimes, teetime, open]);


    useEffect(() => {
        const source = axios.CancelToken.source();
        if (open) {
            void previewEditShotgun(source.token);
        } else {
            setErrors({
                duration: false,
                lead_time: false,
                message: "",
            });
        }
        return () => source.cancel();
    }, [state?.start_time, state?.lead_time, state?.duration]);

    async function previewEditShotgun(cancelToken: CancelToken) {
        const durationError = !state?.duration;
        const leadTimeError = !state?.lead_time;
        if (durationError || leadTimeError) {
            setErrors(prev => ({...prev, duration: durationError, lead_time: leadTimeError}));
            return;
        }

        const durationTime = state?.duration;
        const durationSplit = durationTime?.split(":");
        const durationMins = +durationSplit[0] * 60 + +durationSplit[1];
        const leadTime = state?.lead_time;
        const leadTimeSplit = leadTime?.split(":");
        const leadTimeMins = +leadTimeSplit[0] * 60 + +leadTimeSplit[1];

        const res = await GetEditShotgunPreview({
            start_time: state?.start_time,
            duration: durationMins,
            lead_time: leadTimeMins,
            shotgun_application_id: teetime?.slots[0]?.shotgun_application_id,
            tee_sheet_id: teetime?.slots[0]?.tee_sheet_id,
        }, false);
        if (res?.status === StatusCode.OK) {
            setErrors({
                duration: res?.data?.key ? res?.data?.key === "duration" : false,
                lead_time:res?.data?.key ? res?.data?.key === "lead_time" : false,
                message: res?.data?.message ?? "",
            });
        } else {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
        }
    }

    const leagues = leaguesHook.displayItems;
    const tournaments = tournamentsHook.displayItems;

    function handleShotgunTimePicker(value: string, property: string) {
        setState(prev => ({ ...prev, [property]: value }));
    }

    async function calculateShotgunValues(teeTimes: ITeeTimeNew[], cancelToken: CancelToken) {
        setLoading(true);
        const shotgunTeeTimes = teeTimes?.filter(
            teeTime =>
                teeTime?.slots[0]?.shotgun_application_id && teeTime?.slots[0]?.shotgun_application_id === teetime?.slots[0]?.shotgun_application_id &&
                (
                    teeTime?.slots[0]?.blocked_type === "shotgun" ||
                    teeTime?.slots[0]?.blocked_type === "shotgun_crossover"
                ),
        );

        if (!shotgunTeeTimes.length) {
            setLoading(false);
            return;
        }

        // Shotgun blocked times (to detetmine lead time)
        const shotgunBlocked = teeTimes?.filter(
            teeTime => teeTime?.slots[0]?.shotgun_application_id && teeTime?.slots[0]?.shotgun_application_id === teetime?.slots[0]?.shotgun_application_id
                && teeTime?.slots[0]?.blocked_type === "shotgun_blocked",
        );

        // Shotgun disabled times (to determine duration)
        const shotgunDisabled = teeTimes?.filter(
            teeTime => teeTime?.slots[0]?.shotgun_application_id && teeTime?.slots[0]?.shotgun_application_id === teetime?.slots[0]?.shotgun_application_id
                && teeTime?.slots[0]?.blocked_type === "shotgun_disabled",
        );

        const startTime = shotgunDisabled
            ?.map(teeTime => teeTime.start_time)
            ?.sort()[0];

        const lastTime = shotgunDisabled
            ?.map(teeTime => teeTime.start_time)
            .sort()[shotgunDisabled?.length - 1];

        const blockedTime = shotgunBlocked
            ?.map(teeTime => teeTime.start_time)
            ?.sort()[0];

        const leadTime = blockedTime
            ? calculateTimeDifference(blockedTime, startTime)
            : "00:00";

        const duration = startTime
            ? calculateTimeDifference(startTime, lastTime)
            : "00:00";

        const tournamentId = shotgunTeeTimes?.find(
            teeTime => teeTime?.slots[0]?.tournament_id,
        )?.slots[0]?.tournament_id ?? null;

        const leagueId = shotgunTeeTimes?.find(
            teeTime => teeTime?.slots[0]?.league_id,
        )?.slots[0]?.league_id ?? null;

        let tournament = null;
        let league = null;

        if (tournamentId) {
            const tournamentRes = await GetTournament({ id: tournamentId }, false, cancelToken);
            if (tournamentRes?.status === StatusCode.OK) {
                tournament = tournamentRes?.data[0];
            }
        } else if (leagueId) {
            const leagueRes = await GetLeague({ id: leagueId }, false, cancelToken);
            if (leagueRes?.status === StatusCode.OK) {
                league = leagueRes?.data[0];
            }
        }

        ReactDOM.unstable_batchedUpdates(() => {
            setLoading(false);
            setState(prev => ({
                ...prev,
                selectedLeague: league,
                selectedTournament: tournament,
                lead_time: leadTime,
                duration: duration,
                start_time: startTime,
            }));
        });

    }

    function calculateTimeDifference(start: string, end: string) {
        const startMoment = moment(start, "HH:mm");
        const endMoment = moment(end, "HH:mm");

        const minutes = endMoment.diff(startMoment, "minutes");

        return moment
            .utc(minutes * 60 * 1000)
            .format("HH:mm");
    }

    function handleOnOk() {
        void onOk(
            {
                start_time: state?.start_time,
                lead_time: state?.lead_time,
                duration: state?.duration,
                tournament_id: state?.selectedTournament?.id ?? null,
                league_id: state?.selectedLeague?.id ?? null,
                shotgun_application_id: teetime?.slots[0]?.shotgun_application_id,
                tee_sheet_id: teetime?.slots[0]?.tee_sheet_id,
            },
        );
    }

    return (
        <Sheet
            title={t("Edit shotgun")}
            size="small"
            open={open}
            onCancel={onClose}
            onOk={handleOnOk}
            okText={t("Save")}
            overflow
            okDisabled={loading || !state.duration || !state.lead_time || !state.start_time || errors?.duration || errors?.lead_time}
        >
            <FormLayout>
                {loading ? (
                    <>
                        <FormLayout.Group>
                            <SkeletonBar width="100%" height="2rem" />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <SkeletonBar width="100%" height="2rem" />
                            <SkeletonBar width="100%" height="2rem" />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <SkeletonBar width="100%" height="2rem" />
                            <SkeletonBar width="100%" height="2rem" />
                        </FormLayout.Group>
                    </>
                ) :
                    (<>
                        <FormLayout.Group>
                            <TimePick
                                value={state.start_time}
                                onChange={timeString => setState(prev => ({ ...prev, start_time: timeString }))}
                                label={t("Start Time")}
                                size="large"
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            {state.selectedTournament ? (
                                <div>
                                    <div>
                                        <p className="event-label">{t('Tournament')}</p>
                                    </div>

                                    <div className="selected-container">
                                        <div className="event-name">
                                            <div>{state?.selectedTournament?.name}</div>

                                            <div>
                                                <button className="" onClick={() => {
                                                    setState(prevState => ({
                                                        ...prevState,
                                                        selectedTournament: null,
                                                    }));
                                                }}>
                                                    <Icon icon={faTimes} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <Select
                                        showSearch
                                        label={t("Tournament")}
                                        onSearch={(value: string) => tournamentsHook.setSearchValue(value)}
                                        onChange={(_, tournament: ITournament) => {
                                            setState(prevState => ({ ...prevState, selectedTournament: tournament }));
                                        }
                                        }
                                        placeholder={t("Search tournament...")}
                                        allowClear
                                        searchValue={tournamentsHook.searchValue}
                                        showDropDownOnFocus={true}
                                        searching={tournamentsHook.loading}
                                        disabled={!!state?.selectedLeague}
                                    >
                                        {tournaments?.filter(tournament => !moment(tournament.date).isBefore(moment(), "day"))
                                            .map((tournament: any, index: number) => {
                                                return (
                                                    <Option key={index} value={tournament.id} extraValues={tournament}>
                                                        <div className="flex items-center">
                                                            <span>{tournament.name}</span>
                                                            <span className="shotgun-tournament-date">
                                                                {moment(tournament.date as MomentInput).format("LL")}
                                                            </span>
                                                        </div>
                                                    </Option>
                                                );
                                            })}

                                        <Option disabled value={-2} key={-2} name={""}>
                                            <div>
                                                <hr style={{ margin: "0px" }} />
                                            </div>
                                        </Option>

                                        {tournaments?.filter(tournament => moment(tournament.date).isBefore(moment(), "day"))
                                            .map((tournament: any, index: number) => {
                                                return (
                                                    <Option key={index} value={tournament.id} extraValues={tournament}>
                                                        <div className="flex items-center">
                                                            <span>{tournament.name}</span>
                                                            <span className="shotgun-tournament-date">
                                                                {moment(tournament.date as MomentInput).format("LL")}
                                                            </span>
                                                        </div>
                                                    </Option>
                                                );
                                            })}
                                    </Select>
                                </div>
                            )}
                            {state?.selectedLeague ? (
                                <div>
                                    <div>
                                        <p className="event-label">{t('League')}</p>
                                    </div>

                                    <div className="selected-container">
                                        <div className="event-name">
                                            <div>{state?.selectedLeague?.name}</div>

                                            <div>
                                                <button className="" onClick={() => {
                                                    setState(prevState => ({ ...prevState, selectedLeague: null }));
                                                }}>
                                                    <Icon icon={faTimes} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <Select
                                        showSearch
                                        label={t("League")}
                                        onSearch={(value: string) => leaguesHook.setSearchValue(value)}
                                        onChange={(_, league: ILeague) => {
                                            setState(prevState => ({ ...prevState, selectedLeague: league }));
                                        }}
                                        placeholder={t("Search league...")}
                                        allowClear
                                        searchValue={leaguesHook.searchValue}
                                        showDropDownOnFocus={true}
                                        searching={leaguesHook.loading}
                                        disabled={!!state?.selectedTournament}
                                    >
                                        {leagues?.filter(league => league.start_date && !moment(league.start_date).isBefore(moment(), "day"))
                                            ?.map((league: any, index: number) => {
                                                return (
                                                    <Option value={league?.id} key={index} extraValues={league}>
                                                        <div className="">
                                                            <span className="">{league.name}</span>
                                                            <span className="shotgun-tournament-date">
                                                                {moment(league.start_date as MomentInput).format("YYYY")}
                                                            </span>
                                                        </div>
                                                    </Option>
                                                );
                                            })}

                                        <Option disabled value={-2} key={-2} name={""}>
                                            <div>
                                                <hr style={{ margin: "0px" }} />
                                            </div>
                                        </Option>

                                        {leagues?.filter(league => !league.start_date || moment(league.start_date).isBefore(moment(), "day"))
                                            ?.map((league: any, index: number) => {
                                                return (
                                                    <Option value={league?.id} key={index} extraValues={league}>
                                                        <div className="">
                                                            <span className="">{league.name}</span>
                                                            {league?.start_date && (
                                                                <span className="shotgun-tournament-date">
                                                                    {moment(league.start_date as MomentInput).format("YYYY")}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </Option>
                                                );
                                            })}
                                    </Select>
                                </div>
                            )}
                        </FormLayout.Group>
                        <FormLayout.Group alignStart>
                            <TimePick
                                value={state.duration}
                                onChange={(timeString: string) => handleShotgunTimePicker(timeString, "duration")}
                                label={t("Duration")}
                                hideMeridiem={12}
                                size="large"
                                error={errors?.duration}
                            />
                            <TimePick
                                value={state.lead_time}
                                onChange={(timeString: string) => handleShotgunTimePicker(timeString, "lead_time")}
                                label={t("Lead Time")}
                                hideMeridiem={12}
                                size="large"
                                error={errors?.lead_time}
                            />
                        </FormLayout.Group>
                        {errors?.message ? (
                            <FormLayout.Group>
                                <Callout title={t("Shotgun cannot be updated")} type="error" content={errors?.message}/>
                            </FormLayout.Group>
                        ) : null}
                    </>)}
            </FormLayout>
        </Sheet>
    );
}